import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { formatDateTime } from '@/features/common/formatDate';
import { eventPath } from '@/features/publicsite/eventPath';
import { eventCardMeta } from '@/features/publicsite/events/eventCardMeta';
import { EVENT_KIND_LABEL } from '@/features/publicsite/events/eventKind';
import { eventTextLang } from '@/features/publicsite/events/eventLanguage';
import { EventMarkerBadge } from '@/features/publicsite/events/EventMarkerBadge';
import { eventMarker, PUBLIC_EVENT_STATUS } from '@/features/publicsite/events/eventStatus';
import type { EventCardProps } from '@/features/publicsite/types/events/EventCardProps';
import { publicEvents } from '@/i18n/dictionaries/publicEvents';
import { useTranslation } from '@/i18n/useTranslation';

/**
 * One event on the /evenimente calendar.
 *
 * Not CatalogCard, for the reason FundingCard is not: this card needs a footer, and CatalogCard has
 * no slot for one. The kind sits opposite the title, as a measure's status does; then the title,
 * the marker, and the themes, organiser and place that tell apart events with one title. The date
 * and time — once, which the old calendar item printed three ways without the hour — sit at the
 * foot, under a rule, where every card in a row has them level: the site's own .card__foot, whose
 * margin-top: auto keeps it at the bottom of cards the grid stretches to one height.
 */
export function EventCard({ event: e }: EventCardProps) {
  const { t, language } = useTranslation(publicEvents);
  const kind = t(EVENT_KIND_LABEL[e.kind]);
  const meta = eventCardMeta(e, language);
  return (
    <Link className="card event-card" to={eventPath(e)}>
      <span className="event-card__head">
        <span className="badge">
          {e.status === PUBLIC_EVENT_STATUS.archived ? `${kind} · ${t('mediaArchiveTag')}` : kind}
        </span>
      </span>
      {/* <h2>: the calendar is one flat list with no headings of its own, so a card's title is
        the level directly under the page's <h1>. card.css sizes it as a card heading, not as a
        section heading, so the level is the document outline's business alone. */}
      <h2 lang={eventTextLang(e, 'title', language)}>{e.title}</h2>
      {/* In a block of its own: the card lays its children out as a column and stretches each
        one, which drew the pill as a bar across the card. Only when there is a marker, so the
        other cards get no empty gap. */}
      {eventMarker(e) && (
        <div>
          <EventMarkerBadge event={e} />
        </div>
      )}
      {meta.length > 0 && (
        <p className="muted">
          {meta.map((part, i) => (
            <Fragment key={part.key}>
              {i > 0 && ' · '}
              <span lang={part.lang}>{part.text}</span>
            </Fragment>
          ))}
        </p>
      )}
      <span className="card__foot">
        <span className="card__count">{formatDateTime(e.startsAt, language)}</span>
        {/* The arrow the catalogue tiles end on (CatalogCards), as they draw it: a glyph sized
          by .card__arrow. Hidden from assistive technology, which would read it into the
          link's name as "right arrow". */}
        <span className="card__arrow" aria-hidden="true">
          →
        </span>
      </span>
    </Link>
  );
}
